import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { translations, LanguageCode } from './translations';

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: (key: string) => string;
}

const defaultValue: LanguageContextType = {
  language: 'it',
  setLanguage: () => {},
  t: (key: string) => key,
};

const LanguageContext = createContext<LanguageContextType>(defaultValue);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Cerca se l'utente ha già una preferenza di lingua salvata nel localStorage
  const getSavedLanguage = (): LanguageCode => {
    const saved = localStorage.getItem('language');
    return (saved === 'it' || saved === 'en') ? saved : 'it';
  };

  const [language, setLanguageState] = useState<LanguageCode>(getSavedLanguage());

  // Funzione per cambiare la lingua e salvarla nel localStorage
  const setLanguage = (lang: LanguageCode) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang;
  };

  // Funzione per ottenere la traduzione in base alla chiave
  const t = (key: string): string => {
    const translation = translations[key];
    
    if (!translation) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    
    return translation[language] || key;
  };

  // Imposta l'attributo lang sul documento HTML quando la lingua cambia
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook personalizzato per accedere al contesto di lingua
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
}; 